import { lazy, Suspense, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { eventsApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { Spinner, resolveAssetUrl } from "@/components/ui";
import { BoothProvider, brandingStyle, useBooth } from "./BoothContext";
import type { EventConfig } from "@/lib/types/config";
import { BoothChrome } from "./BoothChrome";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import { ModeScreen } from "./screens/ModeScreen";
import { ParticipantScreen } from "./screens/ParticipantScreen";
import { VibeScreen } from "./screens/VibeScreen";
import { CaptureScreen } from "./screens/CaptureScreen";
const SnapshotSessionScreen = lazy(() => import("./screens/SnapshotSessionScreen").then((module) => ({ default: module.SnapshotSessionScreen })));
import { ProcessingScreen } from "./screens/ProcessingScreen";
import { ResultScreen } from "./screens/ResultScreen";
import { CompletionScreen } from "./screens/CompletionScreen";
import { NotFoundPage } from "./NotFoundPage";

/**
 * The booth for one event, served at its own slug (/negus-malt, /habesha, …).
 *
 * This is the link the admin console hands out per event. `?preview=1` layers
 * the unpublished draft, which is what the Site Builder's device frame loads.
 */
export function BoothApp() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  // ?screen=result lets the Site Builder show a screen that normally sits
  // behind a camera capture — otherwise an operator sets a price and has no
  // way to see it.
  const previewScreen = preview ? params.get("screen") : null;

  const request = useAsync(() => eventsApi.config(slug, preview), [slug, preview]);

  useEffect(() => {
    const config = request.data;
    if (!config) return;

    // The booth is the customer's product, not ours: the tab title and favicon
    // come from their brand.
    const name = config.event.branding?.app_name
      ?? config.event.brand?.name
      ?? config.event.name;
    if (name) document.title = name;

    const favicon = config.event.brand?.favicon_url;
    if (favicon) {
      const link = document.querySelector<HTMLLinkElement>("link[rel='icon']")
        ?? document.head.appendChild(Object.assign(document.createElement("link"), { rel: "icon" }));
      link.href = favicon;
    }
  }, [request.data]);

  if (request.loading) {
    return (
      <div className="grid h-full place-items-center bg-ink-850">
        <Spinner size={28} className="text-accent" />
      </div>
    );
  }

  if (request.error || !request.data) {
    return (
      <NotFoundPage
        title="Booth unavailable"
        message={
          request.error?.message?.includes("not")
            ? "No published event lives at this link. Check the URL, or publish the event from the admin console."
            : request.error?.message ?? "Could not load this booth."
        }
      />
    );
  }

  // An unpublished event still resolves in preview mode, which is exactly what
  // the operator needs while building it.
  if (request.data.event.status !== "published" && !preview) {
    return (
      <NotFoundPage
        title="This booth isn't live yet"
        message="The event exists but hasn't been published. Publish it from the Site Builder to open the doors."
      />
    );
  }

  return (
    <BoothProvider config={request.data} slug={slug}>
      <BoothShell preview={preview} previewScreen={previewScreen} />
    </BoothProvider>
  );
}

function BoothShell({ preview, previewScreen }: { preview: boolean; previewScreen: string | null }) {
  const booth = useBooth();

  // Jump to the requested screen once, seeding whatever that screen needs.
  useEffect(() => {
    if (!previewScreen) return;

    if (previewScreen === "result") {
      booth.showResult({
        shareToken: "preview",
        url: PREVIEW_PHOTO,
        actions: previewActions(booth.config),
      });
    } else {
      booth.go(previewScreen as Parameters<typeof booth.go>[0]);
    }
    // Seeding once is the point; re-running would fight the operator's clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewScreen]);

  return (
    <div
      className="booth-root relative h-full overflow-hidden"
      style={{ ...brandingStyle(booth.config), backgroundColor: "var(--booth-primary)" }}
    >
      <BoothBackground />
      <BoothChrome preview={preview} />

      <div className="relative z-10 h-full">
        {booth.screen === "welcome" && <WelcomeScreen />}
        {booth.screen === "mode" && <ModeScreen />}
        {booth.screen === "participants" && <ParticipantScreen />}
        {booth.screen === "vibes" && (booth.mode === "snapshot"
          ? <Suspense fallback={<div className="grid h-full place-items-center text-white">Preparing camera…</div>}><SnapshotSessionScreen /></Suspense>
          : <VibeScreen />)}
        {booth.screen === "capture" && <CaptureScreen />}
        {booth.screen === "processing" && <ProcessingScreen />}
        {booth.screen === "result" && <ResultScreen />}
        {booth.screen === "completion" && <CompletionScreen />}
      </div>
    </div>
  );
}

/** A stand-in photo so the result screen has something to show in preview. */
const PREVIEW_PHOTO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1000">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#f2c9a0"/><stop offset="60%" stop-color="#b5703f"/>
        <stop offset="100%" stop-color="#2a1a12"/></linearGradient></defs>
      <rect width="100%" height="100%" fill="url(#g)"/>
      <circle cx="400" cy="380" r="150" fill="rgba(255,255,255,0.22)"/>
      <text x="50%" y="88%" fill="rgba(0,0,0,0.5)" font-family="Helvetica,Arial"
            font-size="54" font-weight="bold" text-anchor="middle">SAMPLE</text>
    </svg>`,
  );

/**
 * Entitlement states for the preview, derived from the configured prices.
 *
 * Nothing is "granted", so the operator sees the prices exactly as a guest
 * would before paying — which is the whole point of looking.
 */
function previewActions(config: EventConfig) {
  const kind = config.snapshot?.enabled ? "snapshot" : "ai_photo";
  const grid = config.pricing?.grid?.[kind];
  const currency = config.pricing?.currency ?? "ETB";

  const cell = (action: "download" | "print" | "share") => {
    const entry = grid?.[action];
    return {
      cents: entry?.cents ?? 0,
      currency,
      payable: entry?.payable ?? false,
      granted: !(entry?.payable ?? false),
    };
  };

  return { download: cell("download"), print: cell("print"), share: cell("share") };
}

/** The branded background wash sitting under every screen. */
function BoothBackground() {
  const booth = useBooth();
  const branding = booth.config.event.branding ?? {};
  const background = resolveAssetUrl(branding.background_url);
  const isVideo = branding.background_media_type === "video" || /\.(mp4|webm|ogg)(?:$|\?)/i.test(background ?? "");

  if (isVideo && background) {
    return <video aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline style={{ opacity: "var(--booth-bg-opacity)" }}><source src={background} /></video>;
  }

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: "var(--booth-bg-image)",
        opacity: "var(--booth-bg-opacity)",
      }}
    />
  );
}
